// hooks/useTokenDescriptions.js
//
// Fetches on-chain off-chain Metaplex metadata descriptions for a set of
// Solana mints. Uses Helius's getAssetBatch RPC (up to 1000 ids per call,
// chunked to 100 here for safety).
//
// Module-level cache means descriptions persist across re-renders, tab
// switches, and scan category changes — each mint is fetched at most once
// per session unless the fetch fails (in which case it's marked null and
// not retried).

import { useEffect, useSyncExternalStore } from 'react'

const RPC_URL =
  import.meta.env.VITE_RPC_URL ||
  'https://rpc.cheddarterminal.app/rpc'

const BATCH_SIZE = 100

// mint -> string | null   (null means "fetched, no description")
const cache = new Map()
// mints currently being fetched (so concurrent callers dedupe)
const pending = new Set()

// External-store plumbing so React re-renders when the cache grows.
let version = 0
const listeners = new Set()
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn) }
const getSnapshot = () => version
const notify = () => { version++; for (const fn of listeners) fn() }

async function fetchBatch(mints) {
  const need = []
  for (const m of mints) {
    if (m && !cache.has(m) && !pending.has(m)) need.push(m)
  }
  if (need.length === 0) return
  for (const m of need) pending.add(m)

  try {
    for (let i = 0; i < need.length; i += BATCH_SIZE) {
      const ids = need.slice(i, i + BATCH_SIZE)
      try {
        const res = await fetch(RPC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 'tok-desc',
            method: 'getAssetBatch',
            params: { ids },
          }),
        })
        if (!res.ok) {
          for (const m of ids) cache.set(m, null)
          continue
        }
        const json = await res.json()
        const arr = Array.isArray(json.result) ? json.result : []

        const byId = new Map()
        for (const a of arr) {
          if (!a || !a.id) continue
          const d = (a.content?.metadata?.description || '').trim()
          byId.set(a.id, d || null)
        }
        // Mark every requested id, even ones the RPC didn't return
        for (const id of ids) {
          cache.set(id, byId.has(id) ? byId.get(id) : null)
        }
      } catch (err) {
        console.warn('[tokenDesc] batch fetch failed:', err)
        for (const m of ids) cache.set(m, null)
      }
    }
    notify()
  } finally {
    for (const m of need) pending.delete(m)
  }
}

/**
 * Returns the description cache (a Map<mint, string|null>) and
 * triggers a re-render whenever new descriptions arrive.
 *
 * CONTRACT: the returned Map has ONE stable identity for the whole
 * session. Read it with .get() DURING RENDER — the hook re-renders you
 * when the cache grows, so render-time reads are always fresh. Do NOT
 * put the Map in a useMemo/useEffect dependency array: its identity
 * never changes, so the dep never fires and your derived value freezes
 * on whatever the cache held at first render (this silently broke the
 * chart watermark once). Dep on the .get() RESULT instead.
 *
 * @param {string[]} mints  Array of mint addresses to ensure are fetched.
 */
export default function useTokenDescriptions(mints) {
  // Re-render on cache updates
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  // Kick off fetch on mint list changes
  const key = Array.isArray(mints) ? mints.join(',') : ''
  useEffect(() => {
    if (Array.isArray(mints) && mints.length > 0) fetchBatch(mints)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return cache
}